import {
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Switch,
  Divider,
  ListItemIcon,
  ListItemButton,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Palette,
  Brightness4,
  SystemUpdate,
  BrightnessAutoOutlined,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { get } from 'lodash';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { Fragment, useCallback, useMemo } from 'react';

import { useDialog } from '#packages/web/hooks/use-dialog';
import { Theme } from '#packages/web/theme';

import { useLogger } from '#apps/client-web/app/hooks/use-logger';

import type { UserSettings } from '../models/user-settings';
import type { ThemeAppearance, ThemeName } from '../models/theme-settings';

import { SelectThemeDialog } from './select-theme-dialog';
import { SelectThemeAppearanceDialog } from './select-theme-appearance-dialog';

export type UserSettingsFormData = UserSettings;

export type UserSettingsFormProps = {
  form: UseFormReturn<UserSettingsFormData>;
};

/**
 * A mobile-app style user settings form component.
 * Displays settings organized in sections with intuitive controls.
 */
export function UserSettingsForm({ form }: UserSettingsFormProps) {
  const logger = useLogger(
    'features/user-settings/components/user-settings-form',
  );

  const { watch, control, setValue } = form;
  const formData = watch();

  // Memoize theme options based on selected theme name
  const themeOptions = useMemo(
    () => Theme.list[formData.theme.name],
    [formData.theme.name],
  );

  // SelectTheme dialog
  const selectThemeDialog = useDialog<ThemeName>();
  const setThemeName = useCallback(
    (newThemeName: ThemeName) => {
      logger.debug({ newThemeName }, 'Changing theme name');

      setValue('theme.name', newThemeName, { shouldDirty: true });
      selectThemeDialog.close();
    },
    [setValue, logger, selectThemeDialog.close],
  );

  // SelectAppearance dialog
  const selectAppearanceDialog = useDialog<ThemeAppearance>();
  const setThemeAppearance = useCallback(
    (newAppearance: ThemeAppearance) => {
      logger.debug({ newAppearance }, 'Changing theme appearance');

      setValue('theme.appearance', newAppearance);
      selectAppearanceDialog.close();
    },
    [setValue, logger, selectAppearanceDialog.close],
  );

  return (
    <Fragment>
      <Stack sx={{ flexGrow: 1 }}>
        <List
          subheader={
            <ListSubheader component="div" sx={{ bgcolor: 'background.paper' }}>
              General
            </ListSubheader>
          }>
          <Controller
            control={control}
            name="autoUpdate.enabled"
            render={({ field }) => (
              <ListItem>
                <ListItemIcon>
                  <SystemUpdate />
                </ListItemIcon>
                <ListItemText
                  primary="Auto-update"
                  secondary="Automatically install updates when available"
                />
                <Switch edge="end" {...field} checked={field.value} />
              </ListItem>
            )}
          />
        </List>

        <Divider />

        <List
          subheader={
            <ListSubheader component="div" sx={{ bgcolor: 'background.paper' }}>
              Theme
            </ListSubheader>
          }>
          <Controller
            control={control}
            name="theme.name"
            rules={{
              required: { value: true, message: 'Theme name is required' },
            }}
            render={({ field }) => (
              <ListItemButton
                onClick={selectThemeDialog.open.bind(undefined, field.value)}>
                <ListItemIcon>
                  <Palette />
                </ListItemIcon>
                <ListItemText
                  primary="Color"
                  secondary="Choose your preferred theme"
                />
                <Avatar
                  sx={{
                    bgcolor: get(themeOptions, ['palette', 'primary', 'main']),
                  }}>
                  &nbsp;
                </Avatar>
              </ListItemButton>
            )}
          />

          <Controller
            control={control}
            name="theme.appearance"
            rules={{
              required: {
                value: true,
                message: 'Theme appearance is required',
              },
            }}
            render={({ field }) => (
              <ListItemButton
                onClick={selectAppearanceDialog.open.bind(
                  undefined,
                  field.value,
                )}>
                <ListItemIcon>
                  <Brightness4 />
                </ListItemIcon>
                <ListItemText
                  primary="Appearance"
                  secondary="Choose how the app looks"
                />
                {field.value === 'system' && <BrightnessAutoOutlined />}
                {field.value === 'light' && <LightMode />}
                {field.value === 'dark' && <DarkMode />}
              </ListItemButton>
            )}
          />
        </List>
      </Stack>

      <SelectThemeDialog
        open={selectThemeDialog.isOpen}
        onFormCancel={selectThemeDialog.close}
        onFormSubmit={setThemeName}
        initialValue={selectThemeDialog.data}
      />

      <SelectThemeAppearanceDialog
        open={selectAppearanceDialog.isOpen}
        onFormCancel={selectAppearanceDialog.close}
        onFormSubmit={setThemeAppearance}
        initialValue={selectAppearanceDialog.data}
      />
    </Fragment>
  );
}
